import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Save, Search, Wallet, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage } from "@/lib/api";

function formatRibuan(v) {
  const digits = String(v ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

const gKey = (uid) => `g:${uid}`;
const cKey = (uid, cid) => `c:${uid}:${cid}`;

function toInitial(rows, komponen) {
  const init = {};
  rows.forEach((r) => {
    init[gKey(r.user_id)] = String(Math.round(r.gaji_pokok || 0));
    komponen.forEach((k) => {
      const v = r.nilai?.[k.id];
      init[cKey(r.user_id, k.id)] =
        v == null ? "0" : k.sifat === "Persentase" ? String(v) : String(Math.round(v));
    });
  });
  return init;
}

export default function AdminBulkNominal() {
  const [komponen, setKomponen] = useState([]);
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [initial, setInitial] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unitFilter, setUnitFilter] = useState("all");
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll/bulk-nominal");
      setKomponen(data.komponen || []);
      setRows(data.rows || []);
      setUnits(data.units || []);
      const init = toInitial(data.rows || [], data.komponen || []);
      setInitial(init);
      setDraft(init);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const setCell = (key, raw, isPersen) => {
    const val = isPersen
      ? raw.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").slice(0, 6)
      : raw.replace(/\D/g, "").slice(0, 12);
    setDraft((d) => ({ ...d, [key]: val }));
  };

  const dirtyKeys = useMemo(
    () => new Set(Object.keys(draft).filter((k) => (draft[k] ?? "") !== (initial[k] ?? ""))),
    [draft, initial]
  );

  const dirtyRowIds = useMemo(() => {
    const ids = new Set();
    dirtyKeys.forEach((k) => {
      const uid = k.startsWith("g:") ? k.slice(2) : k.split(":")[1];
      ids.add(uid);
    });
    return ids;
  }, [dirtyKeys]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (unitFilter !== "all" && r.unit_id !== unitFilter) return false;
      if (!term) return true;
      return (
        (r.nama || "").toLowerCase().includes(term) ||
        (r.npp || "").toLowerCase().includes(term)
      );
    });
  }, [rows, unitFilter, q]);

  async function simpanSemua() {
    if (dirtyRowIds.size === 0) {
      toast.info("Tidak ada perubahan untuk disimpan");
      return;
    }
    const updates = [];
    rows.forEach((r) => {
      if (!dirtyRowIds.has(r.user_id)) return;
      const upd = { user_id: r.user_id, komponen: [] };
      if (dirtyKeys.has(gKey(r.user_id))) {
        upd.gaji_pokok = parseInt((draft[gKey(r.user_id)] || "0").replace(/\D/g, ""), 10) || 0;
      }
      komponen.forEach((k) => {
        const key = cKey(r.user_id, k.id);
        if (!dirtyKeys.has(key)) return;
        const raw = draft[key] || "0";
        const nilai = k.sifat === "Persentase"
          ? parseFloat(raw || "0") || 0
          : parseInt(raw.replace(/\D/g, ""), 10) || 0;
        upd.komponen.push({ komponen_id: k.id, nilai_override: nilai });
      });
      updates.push(upd);
    });

    setSaving(true);
    try {
      const { data } = await api.post("/payroll/bulk-nominal", { updates });
      toast.success(`${data.tersimpan} pegawai diperbarui`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const changedCount = dirtyRowIds.size;

  return (
    <div className="p-4 sm:p-8 max-w-[1600px] mx-auto" data-testid="page-bulk-nominal">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi Payroll</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Update Nominal Massal
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Isi <b>Gaji Pokok</b> dan nominal komponen tetap untuk banyak pegawai sekaligus dalam satu tabel —
            tanpa membuka profil satu per satu. Komponen otomatis (Tunjangan Cuti tahunan &amp; Insentif tidak tetap)
            tidak ditampilkan di sini.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} disabled={loading || saving} className="gap-2" data-testid="bulk-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          <Button
            onClick={simpanSemua}
            disabled={saving || changedCount === 0}
            className="bg-teal-700 hover:bg-teal-800 gap-2"
            data-testid="bulk-save-btn"
          >
            <Save size={14} />
            {saving ? "Menyimpan…" : `Simpan Semua${changedCount ? ` (${changedCount})` : ""}`}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Unit Kerja</Label>
          <Select value={unitFilter} onValueChange={setUnitFilter}>
            <SelectTrigger className="mt-1.5" data-testid="bulk-unit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Unit</SelectItem>
              {units.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Cari Pegawai</Label>
          <div className="relative mt-1.5">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nama atau NPP…"
              className="pl-9"
              data-testid="bulk-search"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-slate-500 py-14">Memuat…</div>
      ) : komponen.length === 0 ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="bulk-empty-komponen">
          <div className="font-semibold text-slate-700">Belum ada komponen yang bisa diisi nominalnya</div>
          <div className="text-xs text-slate-500 mt-1">
            Tambahkan komponen bersifat <b>Tetap / Persentase / Berbasis Kehadiran / Berbasis Lembur / Tidak Tetap - Nominal</b> di menu <b>Master Komponen</b>.
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="bulk-empty-rows">
          <div className="font-semibold text-slate-700">Tidak ada pegawai yang cocok dengan filter</div>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="sticky left-0 bg-slate-50 z-20 uppercase text-[10px] tracking-widest font-bold text-slate-500 min-w-[220px] shadow-[8px_0_10px_-6px_rgba(15,23,42,0.22)] border-r border-slate-200">
                    NPP &amp; Nama Pegawai
                  </TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">
                    Gaji Pokok
                  </TableHead>
                  {komponen.map((k) => (
                    <TableHead key={k.id} className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">
                      {k.nama}
                      {k.tipe === "Potongan" ? <span className="text-rose-600"> (pot.)</span> : null}
                      {k.tipe === "Beban Perusahaan" ? <span className="text-indigo-600"> (beban)</span> : null}
                      <div className="text-[9px] font-medium normal-case tracking-normal text-slate-400">{k.satuan}</div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const isDirtyRow = dirtyRowIds.has(r.user_id);
                  return (
                    <TableRow
                      key={r.user_id}
                      className={`border-b border-slate-100 ${isDirtyRow ? "bg-amber-50/40" : ""}`}
                      data-testid={`bulk-row-${r.user_id}`}
                    >
                      <TableCell className={`sticky left-0 z-10 min-w-[220px] shadow-[8px_0_10px_-6px_rgba(15,23,42,0.22)] border-r border-slate-200 ${isDirtyRow ? "bg-amber-50" : "bg-white"}`}>
                        <div className="font-semibold text-slate-900 whitespace-nowrap">{r.nama}</div>
                        <div className="text-[11px] text-slate-500 font-mono-clock">
                          {r.npp}{r.unit_nama ? ` · ${r.unit_nama}` : ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="relative w-40">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400">Rp</span>
                          <Input
                            inputMode="numeric"
                            className={`h-9 pl-7 text-sm font-mono-clock ${dirtyKeys.has(gKey(r.user_id)) ? "border-amber-400 bg-amber-50" : ""}`}
                            value={formatRibuan(draft[gKey(r.user_id)] ?? "")}
                            onChange={(e) => setCell(gKey(r.user_id), e.target.value, false)}
                            placeholder="0"
                            data-testid={`bulk-gaji-${r.user_id}`}
                          />
                        </div>
                      </TableCell>
                      {komponen.map((k) => {
                        const key = cKey(r.user_id, k.id);
                        const isPersen = k.sifat === "Persentase";
                        const dirty = dirtyKeys.has(key);
                        return (
                          <TableCell key={k.id}>
                            <div className="relative w-36">
                              {isPersen ? (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400">%</span>
                              ) : (
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400">Rp</span>
                              )}
                              <Input
                                inputMode={isPersen ? "decimal" : "numeric"}
                                className={`h-9 text-sm font-mono-clock ${isPersen ? "pr-6" : "pl-7"} ${dirty ? "border-amber-400 bg-amber-50" : ""}`}
                                value={isPersen ? (draft[key] ?? "") : formatRibuan(draft[key] ?? "")}
                                onChange={(e) => setCell(key, e.target.value, isPersen)}
                                placeholder="0"
                                data-testid={`bulk-cell-${r.user_id}-${k.id}`}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1.5">
            <Info size={12} /> Sel yang diubah ditandai kuning. Klik <b>Simpan Semua</b> untuk menyimpan seluruh perubahan sekaligus.
            Perubahan pada halaman/filter lain tetap tersimpan sampai Anda menyimpan.
          </div>
        </div>
      )}
    </div>
  );
}
